import { api } from './api';
import { logger } from '../utils/logger';

export type ReportType = 'sales_tax' | 'nexus' | 'compliance';

export interface ReportRow {
  jurisdiction: string;
  jurisdiction_name: string;
  taxable_amount: number;
  tax_amount: number;
  transaction_count: number;
  filing_status: string;
  due_date: string | null;
}

export interface ReportTotals {
  taxable_amount: number;
  tax_amount: number;
  transaction_count: number;
  jurisdiction_count: number;
}

export interface ReportSummary {
  report_type: ReportType;
  period: { start_date: string; end_date: string };
  totals: ReportTotals;
  rows: ReportRow[];
}

export class ReportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReportError';
  }
}

/** Fetch the per-jurisdiction report for a date range from the API. */
export async function fetchReportSummary(
  startDate: string,
  endDate: string,
  reportType: ReportType
): Promise<ReportSummary> {
  try {
    const response = await api.get('/api/v1/reports/summary', {
      params: {
        start_date: startDate,
        end_date: endDate,
        report_type: reportType,
      },
    });
    return response.data as ReportSummary;
  } catch (error: any) {
    logger.error('Error fetching tax report:', error);
    if (error.response) {
      if (error.response.status === 401) {
        throw new ReportError('Your session has expired. Please sign in again.');
      }
      if (error.response.status === 400) {
        throw new ReportError(
          error.response.data?.detail || 'Add your company details before running a report.'
        );
      }
      throw new ReportError(
        error.response.data?.detail || `The report could not be loaded (${error.response.status}).`
      );
    }
    if (error.request) {
      throw new ReportError('Could not reach the server. Check your connection and try again.');
    }
    throw new ReportError('The report could not be loaded.');
  }
}
